/*
 * One running camera's line to the frame worker (frame-worker.js). At most one
 * frame is ever on its way: a frame that comes while the worker is busy is let
 * go, so a slow phone reads fewer frames rather than falling behind.
 *
 * Any sign that it isn't working ends it, once, through onFailed, and the
 * caller reads frames on the main thread as the app always did:
 *   - the worker can't load or fails, or this browser refuses the frame;
 *   - no answer within STALE_MS;
 *   - BLANK_RUN answers in a row with no sharpness at all. A browser that hands
 *     over blank bitmaps would otherwise leave the meter at "waiting" for good;
 *     a covered lens reads the same way, and costs only the worker.
 */
export const STALE_MS = 3000;
export const BLANK_RUN = 5;

export function frameWorkerSupported() {
  return typeof Worker !== 'undefined' && typeof createImageBitmap === 'function'
    && typeof OffscreenCanvas !== 'undefined';
}

function makeFrameWorker() {
  return new Worker(new URL('./frame-worker.js', import.meta.url), { type: 'module', name: 'frames' });
}

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** {send(video, width, ask), close()}: `onSample` gets frameMetrics' reading plus `at`; `ask` goes along to the worker. */
export function createFrameAnalyser({ onSample, onFailed, makeWorker = makeFrameWorker }) {
  let worker = null;
  let closed = false;
  let sentAt = null;
  let blanks = 0;
  let seq = 0;

  const close = () => {
    closed = true;
    if (worker && typeof worker.terminate === 'function') worker.terminate();
    worker = null;
  };
  const fail = () => {
    if (closed) return;
    close();
    if (onFailed) onFailed();
  };

  try {
    worker = makeWorker();
    worker.onmessage = (event) => {
      if (closed) return;
      sentAt = null;
      const { at, reading, error } = event.data || {};
      if (error || !reading) {
        fail();
        return;
      }
      blanks = reading.sharpness > 0 ? 0 : blanks + 1;
      if (blanks >= BLANK_RUN) {
        fail();
        return;
      }
      onSample({ ...reading, at });
    };
    worker.onerror = fail;
    worker.onmessageerror = fail;
  } catch {
    closed = true;
    worker = null;
    // Told after the caller has its handle, as every other failure is.
    Promise.resolve().then(() => { if (onFailed) onFailed(); });
  }

  const send = (video, width, ask = {}) => {
    if (closed || !worker) return;
    const t = now();
    if (sentAt !== null) {
      if (t - sentAt > STALE_MS) fail();
      return;
    }
    if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return;
    const w = width;
    const h = Math.max(1, Math.round((video.videoHeight * w) / video.videoWidth));
    sentAt = t;
    seq += 1;
    const id = seq;
    // 'medium' reads as a canvas drawing the video does (within 1% in Chromium; 'low' read 28% higher),
    // so the meter's best seen still holds if this falls back to reading on the main thread.
    createImageBitmap(video, { resizeWidth: w, resizeHeight: h, resizeQuality: 'medium' }).then((bitmap) => {
      if (closed || !worker) {
        bitmap.close();
        return;
      }
      worker.postMessage({ ...ask, id, at: t, bitmap, w, h }, [bitmap]);
    }).catch(fail);
  };

  return { send, close };
}
